import { Minus, TrendingDown, TrendingUp } from "lucide-react";

/** Fill missing calendar days by carrying the last known score forward — a flat line
 * across quiet days is data, not a gap. Days before the first stored snapshot are NOT
 * fabricated: the series starts at the first real data point. */
export function fillDaily(points, days = 30) {
  if (!points?.length) return [];
  if (typeof points[0] === "number" || points[0].date === undefined) return points;
  const byDate = Object.fromEntries(points.map((p) => [p.date, p.score]));
  const out = [];
  let last = null;
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * 86400000).toISOString().slice(0, 10);
    if (byDate[d] !== undefined) last = byDate[d];
    if (last !== null) out.push({ date: d, score: last });
  }
  return out;
}

export default function Sparkline({ points, width = 72, height = 22, testid = "signal-sparkline", title }) {
  const pts = fillDaily(points);
  if (pts.length === 0) return null;
  const scores = pts.map((p) => (typeof p === "number" ? p : p.score));
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = max - min || 1;
  const step = scores.length > 1 ? width / (scores.length - 1) : width;
  const coords = scores
    .map((s, i) => `${(i * step).toFixed(1)},${(height - 2 - ((s - min) / range) * (height - 4)).toFixed(1)}`)
    .join(" ");
  const lastScore = scores[scores.length - 1];
  const firstScore = scores[0];
  const color = lastScore > firstScore ? "#00FF87" : lastScore < firstScore ? "#F87171" : "#00F0FF";
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-testid={testid} className="shrink-0" role="img" aria-label={title}>
      <polyline points={coords} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={((scores.length - 1) * step).toFixed(1)} cy={(height - 2 - ((lastScore - min) / range) * (height - 4)).toFixed(1)} r="2" fill={color} />
    </svg>
  );
}

export function DeltaChip({ delta, testid }) {
  if (delta === null || delta === undefined) return null;
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const cls = delta > 0 ? "border-[#00FF87]/50 text-[#00FF87]" : delta < 0 ? "border-red-500/50 text-red-400" : "border-slate-700 text-slate-500";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-0.5 rounded border px-1 py-0.5 font-mono2 text-[9px] font-bold ${cls}`}>
      <Icon className="h-2.5 w-2.5" />
      {delta > 0 ? "+" : ""}{delta} 7d
    </span>
  );
}
